import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
const zerosApplyOnDate = (date: Date) => {
  const zerosDate = new Date(date);
  zerosDate.setHours(0);
  zerosDate.setMinutes(0);
  zerosDate.setSeconds(0);
  zerosDate.setMilliseconds(0);

  return zerosDate;
};

const timestampFromDatePrev = (date: Date) => {
  const baseDMin = zerosApplyOnDate(new Date(date));
  baseDMin.setDate(0);

  return timestampFromDate(baseDMin);
};

const timestampFromDateNext = (date: Date) => {
  const baseDMin = zerosApplyOnDate(new Date(date));
  baseDMin.setMonth(baseDMin.getMonth() + 1);
  baseDMin.setDate(0);
  // baseDMin.setMonth(baseDMin.getMonth() + 1);

  return timestampFromDate(baseDMin);
};

const timestampFromDate = (date: Date): number => {
  return zerosApplyOnDate(date).getTime();
};

const changeYearOnTs = (dateTs: number, selectedYear: number) => {
  const curDateTs = new Date(dateTs);
  curDateTs.setFullYear(selectedYear);

  return timestampFromDate(curDateTs);
};

const changeMonthOnTs = (dateTs: number, selectedMonth: number) => {
  const curDateTs = new Date(dateTs);
  curDateTs.setMonth(selectedMonth);

  return timestampFromDate(curDateTs);
};

@Component({
  selector: 'ops-date-picker',
  templateUrl: './ops-date-picker.component.html',
  styleUrls: ['./ops-date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OpsDatePickerComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Input() parentId: string;
  @Input() isMultiSelect = false;

  @Input() invertDirection = false;

  private _selectedDates: Array<number> = [];

  @Input() maxSelect?: number;

  @Input() set selectedDates(dates: Array<Date>) {
    this._selectedDates = dates?.map((d: Date) => {
      return timestampFromDate(d);
    });
  }
  get selectedDates(): Array<Date> {
    return this._selectedDates?.map((dTs: number) => {
      return zerosApplyOnDate(new Date(dTs));
    });
  }

  private _minDate: Date = zerosApplyOnDate(new Date('2014-01-01'));
  private _minDay: number = this._minDate.getDate();
  private _minMonth: number = this._minDate.getMonth();
  private _minYear: number = this._minDate.getFullYear();
  private _minTsDate: number = this._minDate.getTime() - 86400000;

  @Input() set minDate(date: Date) {
    this._minDate = zerosApplyOnDate(new Date(date));

    this._minDay = this._minDate.getDate();
    this._minMonth = this._minDate.getMonth();
    this._minYear = this._minDate.getFullYear();
    this._minTsDate = this._minDate.getTime() - 86400000;

    this.availableYears();
  }

  private _maxDate: Date = zerosApplyOnDate(new Date('2034-01-31'));
  private _maxDay: number = this._maxDate.getDate();
  private _maxMonth: number = this._maxDate.getMonth();
  private _maxYear: number = this._maxDate.getFullYear();
  private _maxTsDate: number = this._maxDate.getTime();

  @Input() set maxDate(date: Date) {
    this._maxDate = zerosApplyOnDate(new Date(date));

    this._maxDay = this._maxDate.getDate();
    this._maxMonth = this._maxDate.getMonth();
    this._maxYear = this._maxDate.getFullYear();
    this._maxTsDate = this._maxDate.getTime();

    this.availableYears();
  }

  @Input() startingFocusIndex: number;

  @Input() isOpen?: boolean = false;

  @Output() datesSelected: EventEmitter<{
    date: Array<Date> | Date | null;
    cleared?: boolean;
  }> = new EventEmitter<{
    date: Array<Date> | Date | null;
    cleared?: boolean;
  }>();

  @Output() clearSelectedDates: EventEmitter<void> = new EventEmitter<void>();
  @Output() pickerClosed: EventEmitter<void> = new EventEmitter<void>();

  //should add localization, centralize
  private monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  showSelectedNextIndex = 0;

  //date.Day is irrelevant
  monthDisplayed: { date: Date; name: string } = {
    date: new Date(),
    name: 'first',
  };
  daysDisplayed: Array<Date>;
  dayNames: Array<string> = [];
  monthNamesView: Array<string> = [];

  yBefore = new Date().getFullYear() - 99;

  yearsListView = [...Array(135)].map((_, index) => index + this.yBefore);

  viewType: 'Day' | 'Month' | 'Year' = 'Day';

  private todayDate: Date = zerosApplyOnDate(new Date());
  private tsTodayDate: number = timestampFromDate(this.todayDate);
  private currentDate: number;
  private element: HTMLElement | null;
  private currentFocusId: number;
  private translationSubscription: Subscription;

  constructor(
    private el: ElementRef,
    private translateService: TranslateService
  ) {
    this.element = this.el.nativeElement;
  }

  get isInvertedDirection(): boolean {
    return this.invertDirection;
  }

  //starts with sunday, should have an @Input to custommize starting day
  ngOnInit(): void {
    this.loadTranslatedMonthNames();
    this.availableYears();

    if (!this.startingFocusIndex) {
      this.startingFocusIndex = 0;
    }
    if (!this._selectedDates) {
      this._selectedDates = [];
    }
    this.currentFocusId = this.startingFocusIndex;

    if (this._selectedDates.length > 0) {
      this.currentDate =
        this._selectedDates[this._selectedDates.length - 1] + 0;
    } else {
      this.setDefaultCurrentDate();
    }

    this.changeDisplayedMonth(zerosApplyOnDate(new Date(this.currentDate)));
  }

  ngOnDestroy(): void {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }

  private loadTranslatedMonthNames(): void {
    this.translationSubscription = this.translateService.onLangChange.subscribe(
      () => {
        this.updateMonthNames();
        this.updateDayNames();
      }
    );
    this.updateMonthNames();
    this.updateDayNames();
  }

  private updateMonthNames(): void {
    this.monthNamesView = this.monthNames.map((monthKey) =>
      this.translateService.instant(`datePicker.months.${monthKey}`)
    );

    // Update the current month display if it's already set
    if (this.monthDisplayed && this.monthDisplayed.date) {
      this.monthDisplayed.name =
        this.monthNamesView[this.monthDisplayed.date.getMonth()];
    }
  }

  private updateDayNames(): void {
    const dayKeys = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    this.dayNames = dayKeys.map((dayKey) =>
      this.translateService.instant(`datePicker.days.${dayKey}`)
    );
  }

  get availableMonths(): Array<string> {
    const monthViews = [...this.monthNamesView];
    const currentDateAsDate = zerosApplyOnDate(new Date(this.currentDate));

    if (this._maxYear === this._minYear) {
      return monthViews.slice(this._minMonth, this._maxMonth + 1);
    } else if (this._maxYear === currentDateAsDate.getFullYear()) {
      // console.log('slice max', this._maxMonth + 1);
      return monthViews.slice(0, this._maxMonth + 1);
    } else if (this._minYear === currentDateAsDate.getFullYear()) {
      // console.log('slice min', this._minMonth);
      return monthViews.slice(this._minMonth);
    }
    return monthViews;
  }

  availableYears() {
    const yListSize = this._maxYear - this._minYear + 1;
    this.yearsListView = [...Array(yListSize)].map(
      (_, index) => index + this._minYear
    );
  }

  isTodayDate(date: Date): boolean {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return this.todayDate.getTime() === date.getTime();
  }

  onSelectYear(year: number, event?: any) {
    event?.stopPropagation();
    this.currentDate = changeYearOnTs(this.currentDate, year);
    this.viewType = 'Month';
  }

  onSelectMonth(month: string, event?: any) {
    event?.stopPropagation();
    const monthIndex = this.monthNamesView.indexOf(month);
    this.currentDate = changeMonthOnTs(this.currentDate, monthIndex);
    this.viewType = 'Day';
    this.changeDisplayedMonth(zerosApplyOnDate(new Date(this.currentDate)));
  }

  switchMonth(event?: any) {
    event?.stopPropagation();
    this.viewType = 'Month';
  }

  switchDay(event?: any) {
    event?.stopPropagation();
    this.viewType = 'Day';
  }

  switchYear(event?: any) {
    event?.stopPropagation();
    this.viewType = 'Year';

    this.focusSelectedYear();
  }

  focusSelectedYear() {
    setTimeout(() => {
      const dropdown = this.element.querySelector('.years-grid') as any;
      const highlightedItem = dropdown.querySelector('.selected');
      if (highlightedItem) {
        dropdown.scrollTop = highlightedItem.offsetTop - dropdown.offsetTop;
      }
    }, 20);
  }

  closeFromBackground(event: any) {
    // Check if the click target is within the date-picker-container
    const target = event.target as HTMLElement;
    const container = this.element?.querySelector('.date-picker-container');
    
    if (container && container.contains(target)) {
      // Click was inside the container, don't close
      return;
    }
    
    event.stopPropagation();
    this.close();
  }

  onContainerClick(event: any) {
    // Stop propagation to prevent the background click handler from firing
    event.stopPropagation();
  }

  onContainerTouchStart(event: any) {
    // Stop propagation for touch events on mobile
    event.stopPropagation();
  }

  close(event?: any): void {
    event?.stopPropagation();
    this.isOpen = false;
    this.viewType = 'Day';

    this.pickerClosed.emit();
  }

  get isMaxReached(): boolean {
    if (
      this.maxSelect !== undefined &&
      this.maxSelect !== null &&
      this.maxSelect === this._selectedDates.length
    ) {
      return true;
    }

    return false;
  }

  isDisabled(value: Date): boolean {
    return (
      timestampFromDate(value) > this._maxTsDate ||
      timestampFromDate(value) <= this._minTsDate
    );
  }

  //TODO: revise this, maybe using services or the module to keep state/shared collection is better
  onSelectDate(value: Date, event?: any): void {
    event?.stopPropagation();
    if (
      timestampFromDate(value) > this._maxTsDate ||
      timestampFromDate(value) <= this._minTsDate
    ) {
      return;
    }

    if (this.isMaxReached && !this.isDateInArray(this._selectedDates, value))
      return;

    if (this.isMultiSelect === true) {
      this.toggleDate(value);

      const mapSelectedDates = this.selectedDates.map((d: Date) => {
        return zerosApplyOnDate(new Date(d));
      });

      this.datesSelected.emit({ date: mapSelectedDates });
      return;
    }

    // const dateTimestamp = timestampFromDate(new Date(value));

    this.selectedDates = [new Date(value)];

    const mapSelectedDates = this.selectedDates.map((d: Date) => {
      return zerosApplyOnDate(new Date(d));
    });
    this.datesSelected.emit({ date: mapSelectedDates[0] });
  }

  reset(event?: any) {
    event?.stopPropagation();
    this.setDefaultCurrentDate();
    this.viewType = 'Day';
  }

  onClearSelection(event?: any) {
    event?.stopPropagation();
    this._selectedDates = [];

    this.setDefaultCurrentDate();
    if (this.isMultiSelect) {
      this.datesSelected.emit({
        date: [] as Date[],
        cleared: true,
      });
    } else {
      this.datesSelected.emit({ date: null, cleared: true });
    }
  }

  private toggleDate(newDate: Date) {
    if (this.isDateInArray(this._selectedDates, newDate)) {
      const newDateTs = timestampFromDate(newDate);

      this._selectedDates = this._selectedDates.filter(
        (x: number) => x !== newDateTs
      );

      this.selectedDates = [
        ...this.selectedDates
          .map((d: any) => (d = new Date(d)))
          .sort((a, b) => a.getTime() - b.getTime()),
      ];
      return;
    }
    this._selectedDates.push(timestampFromDate(newDate));
    this._selectedDates = [...this._selectedDates.sort((a, b) => a - b)];
  }

  get isPrevButtonAvailable(): boolean {
    const monthDisplayedPrev = timestampFromDatePrev(this.monthDisplayed.date);
    if (monthDisplayedPrev > this._minTsDate) {
      return true;
    }
    return false;
  }

  get isNextButtonAvailable(): boolean {
    const monthDisplayedNext = timestampFromDateNext(this.monthDisplayed.date);
    if (monthDisplayedNext < this._maxTsDate) {
      return true;
    }
    return false;
  }

  shiftMonthUp(shiftUp: boolean, event?: any) {
    event?.stopPropagation();
    const monthDisplayedNext = timestampFromDateNext(this.monthDisplayed.date);
    const monthDisplayedPrev = timestampFromDatePrev(this.monthDisplayed.date);
    if (monthDisplayedNext >= this._maxTsDate && shiftUp) {
      return;
    } else if (monthDisplayedPrev <= this._minTsDate && !shiftUp) {
      return;
    } else {
      const newMonth = shiftUp
        ? this.monthDisplayed.date.getMonth() + 1
        : this.monthDisplayed.date.getMonth() - 1;

      this.changeDisplayedMonth(
        new Date(this.monthDisplayed.date.getFullYear(), newMonth, 2)
      );
    }
  }

  onKeyAction(key: any, value: any) {
    if (key.keyCode !== 32 && key.keyCode !== 13) {
      //enter and space keys
      return;
    }
    if (value instanceof Date) {
      this.onSelectDate(value as Date);
      document
        .getElementById(this.currentFocusId.toString())
        ?.classList.add('selected');
      return;
    }
    this.shiftMonthUp(value as boolean);
  }

  changeDisplayedMonth(newDate: Date): void {
    this.monthDisplayed = {
      date: newDate,
      name: this.monthNamesView[newDate.getMonth()],
    };
    this.daysDisplayed = this.generateDays(
      newDate.getFullYear(),
      newDate.getMonth()
    );
  }

  private generateDays(year: number, month: number): Array<Date> {
    const result: Array<Date> = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    result.push(...this.getDaysPreviousMonthToDisplay(year, month));
    for (let i = 0; i < daysInMonth; i++) {
      result.push(new Date(year, month, i + 1));
    }
    result.push(
      ...this.getDaysInFollowingMonthToDisplay(year, month, daysInMonth)
    );
    return result;
  }

  private getDaysPreviousMonthToDisplay(
    year: number,
    month: number
  ): Array<Date> {
    const result: Array<Date> = [];

    const firstDayWeekDay = new Date(year, month, 1).getDay();
    if (firstDayWeekDay != 0) {
      const daysInLastMonth = new Date(year, month, 0).getDate();
      for (
        let i = daysInLastMonth - firstDayWeekDay;
        i < daysInLastMonth;
        i++
      ) {
        result.push(new Date(year, month - 1, i + 1));
      }
    }
    return result;
  }

  private getDaysInFollowingMonthToDisplay(
    year: number,
    month: number,
    daysInMonth: number
  ): Array<Date> {
    const result: Array<Date> = [];
    const lastDayWeekDay = new Date(year, month, daysInMonth).getDay();
    if (lastDayWeekDay != 7) {
      for (let i = 1; i < 7 - lastDayWeekDay; i++) {
        result.push(new Date(year, month + 1, i));
      }
    }
    return result;
  }

  isDateInMonth(value: Date): boolean {
    const d = new Date(value);

    if (this.monthDisplayed.date.getMonth() === d.getMonth()) {
      return true;
    }
    return false;
  }

  private isSelectedDay(day: Date): boolean {
    const dayToCheck = timestampFromDate(day);
    return this._selectedDates.indexOf(dayToCheck) > -1;
  }

  //TODO: move this to utilities and refactor so that it doesnt get called milions of times
  private isDateInArray(array: Array<number>, value: Date): boolean {
    const d = timestampFromDate(new Date(value));
    if (!array || array.length == 0) {
      return false;
    }
    if (!array?.length) {
      array = new Array<number>();
    }
    if (array && d) {
      const l = !!(array as Array<number>).find((item: number) => item === d);
      return l;
    }
    return false;
  }

  get isDiffSelected(): boolean {
    const currentDate = zerosApplyOnDate(new Date(this.monthDisplayed.date));
    const diffNextTs = timestampFromDateNext(currentDate);
    const diffPrefTs = timestampFromDatePrev(currentDate);

    for (let i = this._selectedDates.length - 1, l = 0; i >= l; i--) {
      if (
        this._selectedDates[i] >= diffPrefTs &&
        this._selectedDates[i] <= diffNextTs
      ) {
        return true;
      }
    }

    return false;
    // return (
    //   currentDate.getFullYear() !== this.todayDate.getFullYear() ||
    //   currentDate.getMonth() !== this.todayDate.getMonth()
    // );
  }

  isDiffToSelected(dateTs: number): boolean {
    const currentDate = zerosApplyOnDate(new Date(this.monthDisplayed.date));
    const diffNextTs = timestampFromDateNext(currentDate);
    const diffPrefTs = timestampFromDatePrev(currentDate);

    // for (let i = this._selectedDates.length - 1, l = 0; i >= l; i--) {
    if (dateTs < diffPrefTs || dateTs > diffNextTs) {
      return true;
    }
    // }

    return false;
    // return (
    //   currentDate.getFullYear() !== this.todayDate.getFullYear() ||
    //   currentDate.getMonth() !== this.todayDate.getMonth()
    // );
  }

  get isCurrentMonth(): boolean {
    return (
      this.monthDisplayed.date.getFullYear() === this.todayDate.getFullYear() &&
      this.monthDisplayed.date.getMonth() === this.todayDate.getMonth()
    );
  }

  get isShowSelectedAvailable(): boolean {
    if (this._selectedDates.length === 0) {
      return false;
    }

    // if (this.isTodayAvailable) {
    //   return false;
    // }

    // if (this.isDiffSelected) {
    //   return false;
    // }

    return true;
  }

  get isTodayAvailable(): boolean {
    if (
      this.tsTodayDate <= this._maxTsDate &&
      this.tsTodayDate >= this._minTsDate
    ) {
      if (!this.isCurrentMonth) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  setDefaultCurrentDate() {
    if (this.tsTodayDate > this._maxTsDate) {
      this.currentDate = this._maxTsDate + 0;
    } else if (this.isCurrentMonth) {
      this.currentDate = this.tsTodayDate + 0;
    } else if (this.isTodayAvailable) {
      this.currentDate = this.tsTodayDate + 0;
    } else {
      this.currentDate = this._maxTsDate + 0;
    }

    const currDate = zerosApplyOnDate(new Date(this.currentDate));
    this.changeDisplayedMonth(currDate);
  }

  showToday(event?: any) {
    event?.stopPropagation();
    this.changeDisplayedMonth(this.todayDate);
  }

  showSelected() {
    const nextToSelect = this.showSelectedNextIndex + 1;

    this.showSelectedNextIndex =
      this._selectedDates.length - 1 < nextToSelect ? 0 : nextToSelect;

    if (
      this.isDiffToSelected(this._selectedDates[this.showSelectedNextIndex])
    ) {
      const currDate = zerosApplyOnDate(
        new Date(this._selectedDates[this.showSelectedNextIndex])
      );
      this.changeDisplayedMonth(currDate);
    } else {
      this.showSelected();
    }
  }

  @HostListener('document:keyup', ['$event']) onKeyUp(e: any) {
    if (!this.isOpen) return;
    if (e.keyCode === 9) {
      // this.modalService.close(this.id);
      return;
    }
    const internalIndex = this.currentFocusId - this.startingFocusIndex;
    //TODO: refactor into smaller methods
    switch (e.keyCode) {
      case 37: {
        //LEFT arrow
        if (internalIndex > 1) {
          this.currentFocusId--;
          break;
        }
        this.currentFocusId =
          this.startingFocusIndex + this.daysDisplayed.length + 2;
        break;
      }
      case 39: {
        //RIGHT arrow
        if (internalIndex < this.daysDisplayed.length + 2) {
          this.currentFocusId++;
          break;
        }
        this.currentFocusId = this.startingFocusIndex + 1;
        break;
      }
      case 40: {
        //DOWN arrow
        if (internalIndex === 1) {
          //month navigation arrow icon - left
          this.currentFocusId += 2;
          break;
        }
        if (internalIndex === this.daysDisplayed.length + 2 - 6) {
          //first day of last week + 2 for arrow icons
          this.currentFocusId = this.startingFocusIndex + 1;
          break;
        }
        if (internalIndex === this.daysDisplayed.length + 2) {
          //last day of last week + 2 for arrow icons
          this.currentFocusId = this.startingFocusIndex + 2;
          break;
        }
        if (internalIndex > this.daysDisplayed.length + 2 - 7) {
          // any other day of last week
          this.currentFocusId -= this.daysDisplayed.length - 7;
          break;
        }
        this.currentFocusId += 7;
        break;
      }
      case 38: {
        //UP arrow
        if (internalIndex === 1) {
          //navigation arrows - left
          this.currentFocusId =
            this.startingFocusIndex + this.daysDisplayed.length + 2 - 6;
          break;
        }
        if (internalIndex === 2) {
          //navigation arrows - right
          this.currentFocusId =
            this.startingFocusIndex + this.daysDisplayed.length + 2;
          break;
        }
        if (internalIndex === 3) {
          //first day of first week
          this.currentFocusId -= 2;
          break;
        }
        if (internalIndex < 9) {
          //other days of first week
          this.currentFocusId += this.daysDisplayed.length;
        }
        this.currentFocusId -= 7;
        break;
      }
    }
    document.getElementById(this.currentFocusId?.toString())?.focus();
  }

  // Format year with proper Japanese formatting
  formatYear(year: number): string {
    // Handle undefined or null year
    if (!year) {
      return '';
    }
    
    const currentLang = this.translateService.currentLang;
    if (currentLang === 'jp') {
      return `${year}${this.translateService.instant('dateFormatting.yearFormat')}`;
    }
    return year.toString();
  }
}
